import React, { useState } from "react";
import {
	Alert,
	Badge,
	Button,
	Card,
	Col,
	Container,
	Form,
	Row,
	Table,
} from "react-bootstrap";
import AdminLayout from "./AdminLayout";
import {
	updateRidersFromFile,
	updateXcRidersFromFile,
} from "../../services/riderService";
import { assignBrandTeams, parseBrandTeams } from "../../services/brandTeamService";

const SOURCE_FILENAME = "riders_complete_2026_with_images.json";

const EXAMPLE_JSON = `[
  {
    "first_name": "Ian",
    "last_name": "ACKERT",
    "discipline": "XC",
    "country": "CAN",
    "href": "/rider-details/542795",
    "DOB": "06.03.2005",
    "team": "PIVOT CYCLES - OTE",
    "team_short": "OTE",
    "imageUrl": "https://example.com/riders/ian-ackert.jpg"
  },
  {
    "first_name": "Max",
    "last_name": "ALRAN",
    "discipline": "DH",
    "country": "FRA",
    "href": "/rider-details/673156",
    "DOB": "23.03.2007",
    "team": "COMMENCAL/MUC-OFF BY RIDING ADDICTION",
    "team_short": "CMO",
    "imageUrl": ""
  }
]`;

const INITIAL_STEPS = {
	dhImport: { status: "idle", message: "Not started", response: "" },
	xcImport: { status: "idle", message: "Not started", response: "" },
	parseTeams: { status: "idle", message: "Not started", response: "" },
	assignTeams: { status: "idle", message: "Not started", response: "" },
};

const STATUS_VARIANT = {
	idle: "secondary",
	running: "warning",
	success: "success",
	error: "danger",
};

const getErrorMessage = (error) => {
	const apiError = error?.response?.data;
	if (typeof apiError === "string") return apiError;
	if (apiError?.error) return apiError.error;
	if (apiError?.message) return apiError.message;
	return error?.message || "Request failed";
};

const formatResponse = (data) => {
	if (typeof data === "string") return data;
	try {
		return JSON.stringify(data, null, 2);
	} catch (error) {
		return "Unable to format response payload.";
	}
};

const AdminUploadRidersPage = () => {
	const [steps, setSteps] = useState(INITIAL_STEPS);
	const [copyStatus, setCopyStatus] = useState("");
	const [pageError, setPageError] = useState("");

	const stepConfig = [
			{
				id: "dhImport",
				title: "Step 1: Import Downhill Riders",
				endpoint: "POST /riders/update/fromfile/dh",
				whenToRun: "Run when the JSON includes updated DH entries for the active season.",
				expected:
					"Adds new DH riders, updates DH rider team history/image URL, skips junior riders, and marks missing DH riders inactive.",
				action: updateRidersFromFile,
			},
			{
				id: "xcImport",
				title: "Step 2: Import Cross-Country Riders",
				endpoint: "POST /riders/update/fromfile/xc",
				whenToRun: "Run when the JSON includes updated XC entries for the active season.",
				expected:
					"Adds new XC riders, updates XC rider team history/image URL, skips juniors, forces U23 riders inactive, and marks missing XC riders inactive.",
				action: updateXcRidersFromFile,
			},
		{
			id: "parseTeams",
			title: "Step 3: Parse Brand Teams From Riders",
			endpoint: "GET /brandTeams/parseBrandTeams",
			whenToRun:
				"Run after rider imports to ensure both XC and DH team names exist as BrandTeam records.",
			expected:
				"Creates missing brand teams from rider team strings (without removing existing teams).",
			action: parseBrandTeams,
		},
		{
			id: "assignTeams",
			title: "Step 4: Assign Brand Teams To Rider Team History",
			endpoint: "POST /brandTeams/assignBrandTeams",
			whenToRun: "Run after team parsing to attach rider team history for the active season.",
			expected:
				"Matches rider.team to BrandTeam and appends missing teamHistory entries for active season.",
			action: assignBrandTeams,
		},
	];

	const isAnyStepRunning = Object.values(steps).some(
		(step) => step.status === "running",
	);

	const runStep = async (step) => {
		setPageError("");
		setSteps((prev) => ({
			...prev,
			[step.id]: {
				...prev[step.id],
				status: "running",
				message: "Running...",
			},
		}));

		try {
			const res = await step.action();
			const responseData = res?.data ?? {};
			setSteps((prev) => ({
				...prev,
				[step.id]: {
					status: "success",
					message: responseData?.message || "Completed successfully.",
					response: formatResponse(responseData),
				},
			}));
		} catch (error) {
			const message = getErrorMessage(error);
			setSteps((prev) => ({
				...prev,
				[step.id]: {
					status: "error",
					message,
					response: "",
				},
			}));
			setPageError(message);
		}
	};

	const copyExampleJson = async () => {
		setCopyStatus("");
		try {
			await navigator.clipboard.writeText(EXAMPLE_JSON);
			setCopyStatus("Example JSON copied to clipboard.");
		} catch (error) {
			setCopyStatus("Could not copy. Select and copy manually.");
		}
	};

	return (
		<AdminLayout>
			<Container className="py-4">
				<h1 className="text-light mb-3">Upload Riders Workflow</h1>
				<p className="text-light mb-4">
					Use this page to run the rider import pipeline manually, one step at a
					time, and verify output after each call.
				</p>

				<Card className="mb-4">
					<Card.Body>
						<Card.Title>Pipeline Steps</Card.Title>
						<Card.Text>
							Recommended order: DH import, XC import, parse brand teams, then
							assign team history.
						</Card.Text>
						<Table responsive bordered hover>
							<thead>
								<tr>
									<th>Step</th>
									<th>Status</th>
									<th>Last Message</th>
								</tr>
							</thead>
							<tbody>
								{stepConfig.map((step) => (
									<tr key={step.id}>
										<td>{step.title}</td>
										<td>
											<Badge bg={STATUS_VARIANT[steps[step.id].status]}>
												{steps[step.id].status}
											</Badge>
										</td>
										<td>{steps[step.id].message}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Card.Body>
				</Card>

				<Row className="g-4 mb-4">
					{stepConfig.map((step) => (
						<Col lg={6} key={step.id}>
							<Card className="h-100">
								<Card.Body>
									<div className="d-flex justify-content-between align-items-start mb-2">
										<Card.Title className="mb-0">{step.title}</Card.Title>
										<Badge bg="dark">{step.endpoint}</Badge>
									</div>
									<Card.Text className="mb-2">
										<strong>When to run:</strong> {step.whenToRun}
									</Card.Text>
									<Card.Text>
										<strong>Expected outcome:</strong> {step.expected}
									</Card.Text>
									<Button
										variant="primary"
										onClick={() => runStep(step)}
										disabled={isAnyStepRunning}
									>
										{steps[step.id].status === "running"
											? "Running..."
											: "Run Step"}
									</Button>
									{steps[step.id].response ? (
										<Form.Control
											as="textarea"
											readOnly
											rows={7}
											value={steps[step.id].response}
											className="mt-3 font-monospace"
										/>
									) : null}
								</Card.Body>
							</Card>
						</Col>
					))}
				</Row>

				{pageError ? <Alert variant="danger">{pageError}</Alert> : null}

				<Card className="mb-4">
					<Card.Body>
						<Card.Title>JSON Contract</Card.Title>
						<Card.Text>
							Source file used by import routes: <code>{SOURCE_FILENAME}</code>
						</Card.Text>
						<Table responsive bordered>
							<thead>
								<tr>
									<th>Field</th>
									<th>Required</th>
									<th>Description</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>
										<code>first_name</code>
									</td>
									<td>Yes</td>
									<td>Used for matching/inserting riders.</td>
								</tr>
								<tr>
									<td>
										<code>last_name</code>
									</td>
									<td>Yes</td>
									<td>Used for matching/inserting riders.</td>
								</tr>
								<tr>
									<td>
										<code>discipline</code>
									</td>
									<td>Yes</td>
									<td>
										Route filter: <code>DH</code> processed by DH route,{" "}
										<code>XC</code> processed by XC route, others ignored.
									</td>
								</tr>
								<tr>
									<td>
										<code>country</code>
									</td>
									<td>No</td>
									<td>Stored on rider record if provided.</td>
								</tr>
								<tr>
									<td>
										<code>href</code>
									</td>
									<td>No</td>
									<td>Stored on rider record if provided.</td>
								</tr>
								<tr>
									<td>
										<code>DOB</code>
									</td>
									<td>No</td>
									<td>Stored on rider record if provided.</td>
								</tr>
								<tr>
									<td>
										<code>team</code>
									</td>
									<td>No</td>
									<td>Used for brand team matching and team history assignment.</td>
								</tr>
								<tr>
									<td>
										<code>team_short</code>
									</td>
									<td>No</td>
									<td>Used when creating new brand teams from file/rider data.</td>
								</tr>
								<tr>
									<td>
										<code>imageUrl</code>
									</td>
									<td>No</td>
									<td>
										Persisted on new riders; updates existing riders only when
										non-empty.
									</td>
								</tr>
							</tbody>
						</Table>
						<ul>
							<li>File must be a valid JSON array.</li>
							<li>Do not upload truncated JSON; ensure closing brackets exist.</li>
							<li>Use one rider object per array item.</li>
							<li>
								Junior riders are automatically skipped in DH/XC imports based on{" "}
								<code>DOB</code> and active season year.
							</li>
							<li>
								XC only: U23 riders (ages 19-22 in active season year) are
								imported but forced to <code>inactive</code>.
							</li>
						</ul>
					</Card.Body>
				</Card>

				<Card>
					<Card.Body>
						<div className="d-flex justify-content-between align-items-center mb-2">
							<Card.Title className="mb-0">Example JSON</Card.Title>
							<Button variant="outline-secondary" onClick={copyExampleJson}>
								Copy Example
							</Button>
						</div>
						<Form.Control
							as="textarea"
							readOnly
							rows={18}
							value={EXAMPLE_JSON}
							className="font-monospace"
						/>
						{copyStatus ? <Alert className="mt-3 mb-0">{copyStatus}</Alert> : null}
					</Card.Body>
				</Card>
			</Container>
		</AdminLayout>
	);
};

export default AdminUploadRidersPage;
